import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ExternalLink, History, Loader, Trash2 } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { ScanTextInput } from '../../../common/inputs';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { docInterpretationService } from '../../../../services/docInterpretationService';
import type {
  DocInterpretationEvent,
  DocInterpretationItemMap,
  DocInterpretationLogItem,
} from '../../../../types/docInterpretation';
import { DocInterpretationView } from '../working-orders/doc-interpretation/DocInterpretationView';
import { eventValueKey } from './docInterpretationAdminHelpers';

interface DocInterpretationLogProps {
  onBack?: () => void;
}

const PAGE_SIZE = 50;
const fmtDateTime = (value: string | null) => (value ? new Date(value).toLocaleString('nb-NO') : '–');

/**
 * Loggen «Tolkede dokumenter» under Varemottak › Historikk (TW 36423841):
 * dokument, leverandør, tolket med, status og manuelle valg; lærte artikkelkoblinger med
 * mulighet til å fjerne dem.
 */
export const DocInterpretationLog: React.FC<DocInterpretationLogProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const p = 'manager.modules.docInterpretationLog';

  const [tab, setTab] = useState<'documents' | 'itemMaps'>('documents');
  const [status, setStatus] = useState('');
  const [supplierNo, setSupplierNo] = useState('');
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<DocInterpretationLogItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<DocInterpretationLogItem | null>(null);
  const [events, setEvents] = useState<DocInterpretationEvent[] | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [maps, setMaps] = useState<DocInterpretationItemMap[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadLog = useCallback(async () => {
    setError(null);
    try {
      const page = await docInterpretationService.getLog({
        status: status || undefined,
        supplierNo: supplierNo.trim() || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setItems(page.items);
      setTotal(page.total);
      // Valgt dokument byttes med den ferske raden, så hendelsene hentes på nytt (f.eks. etter avgjørelser i visningen)
      setSelected(prev => (prev ? page.items.find(i => i.id === prev.id) ?? prev : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [status, supplierNo, offset]);

  const loadMaps = useCallback(async () => {
    setError(null);
    try {
      setMaps(await docInterpretationService.getItemMaps(supplierNo.trim() || undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [supplierNo]);

  useEffect(() => {
    if (tab === 'documents') loadLog(); else loadMaps();
  }, [tab, loadLog, loadMaps]);

  useEffect(() => {
    setEvents(null);
    if (selected) docInterpretationService.getEvents(selected.id).then(setEvents).catch(err => setError(err.message));
  }, [selected]);

  const removeMap = async (map: DocInterpretationItemMap) => {
    const question = t(`${p}.itemMaps.confirmDelete`, { '0': map.supplierItemNo, '1': map.itemNo },
      `Fjerne koblingen ${map.supplierItemNo} → ${map.itemNo}?`);
    if (!window.confirm(question)) return;
    try {
      await docInterpretationService.deleteItemMap(map.id);
      await loadMaps();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (openId != null) {
    return (
      <DocInterpretationView
        preselectedOrders={[]}
        initialResultId={openId}
        onClose={() => { setOpenId(null); loadLog(); }}
      />
    );
  }

  const statusText = (s: string) => t(`${p}.status.${s}`, {}, s);
  const eventText = (e: string) => t(`${p}.events.${e}`, {}, e);
  const valueText = (eventType: string, value: string | null) => {
    const key = eventValueKey(eventType, value);
    return key ? t(`${p}.values.${key}`, {}, value ?? '') : value ?? '–';
  };
  const lineText = (e: DocInterpretationEvent) =>
    e.lineSeq != null
      ? t(`${p}.line`, { '0': e.lineSeq, '1': e.lineItemNo ?? '' }, `linje ${e.lineSeq} · ${e.lineItemNo ?? ''}`)
      : t(`${p}.lineId`, { '0': e.lineId ?? '' }, `linje-ID ${e.lineId}`);
  const tabClass = (active: boolean) =>
    `px-4 py-2 text-sm font-semibold border-b-2 ${active ? 'border-blue-500 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-6 pt-4 bg-white border-b">
        <div className="flex items-center space-x-4 mb-3">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <History size={24} />
            <span>{t(`${p}.title`, {}, 'Tolkede dokumenter')}</span>
          </h1>
        </div>
        <div className="flex gap-2">
          <button type="button" className={tabClass(tab === 'documents')} onClick={() => setTab('documents')}>
            {t(`${p}.tabs.documents`, {}, 'Dokumenter')}
          </button>
          <button type="button" className={tabClass(tab === 'itemMaps')} onClick={() => setTab('itemMaps')}>
            {t(`${p}.tabs.itemMaps`, {}, 'Lærte koblinger')}
          </button>
        </div>
      </div>

      <div className="px-6 py-3 bg-white border-b flex flex-wrap items-end gap-3">
        {tab === 'documents' && (
          <label className="text-sm">
            <span className="block text-gray-600">{t(`${p}.filters.status`, {}, 'Status')}</span>
            <select value={status} onChange={e => { setOffset(0); setStatus(e.target.value); }}
              className="mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-white">
              <option value="">{t(`${p}.filters.allStatuses`, {}, 'Alle')}</option>
              {['Interpreted', 'Approved', 'Undone', 'ReceiptStarted', 'Failed'].map(s => (
                <option key={s} value={s}>{statusText(s)}</option>
              ))}
            </select>
          </label>
        )}
        <label className="text-sm">
          <span className="block text-gray-600">{t(`${p}.filters.supplierNo`, {}, 'Leverandørnr.')}</span>
          <ScanTextInput value={supplierNo} onChange={e => { setOffset(0); setSupplierNo(e.target.value); }}
            className="mt-1 w-40 px-3 py-2 border border-gray-300 rounded-lg" />
        </label>
      </div>

      {error && (
        <div className="mx-6 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-center gap-2">
          <AlertCircle size={16} />{error}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto p-6">
        {tab === 'documents' ? (
          items == null ? (
            <div className="flex items-center justify-center h-32"><Loader className="animate-spin" size={20} /></div>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500">{t(`${p}.empty`, {}, 'Ingen tolkede dokumenter.')}</p>
          ) : (
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="lg:flex-1 bg-white border border-gray-200 rounded-lg overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                      <th className="px-3 py-2">{t(`${p}.columns.created`, {}, 'Tolket')}</th>
                      <th className="px-3 py-2">{t(`${p}.columns.document`, {}, 'Dokument')}</th>
                      <th className="px-3 py-2">{t(`${p}.columns.supplier`, {}, 'Leverandør')}</th>
                      <th className="px-3 py-2">{t(`${p}.columns.method`, {}, 'Tolket med')}</th>
                      <th className="px-3 py-2">{t(`${p}.columns.status`, {}, 'Status')}</th>
                      <th className="px-3 py-2 text-right">{t(`${p}.columns.lines`, {}, 'Linjer')}</th>
                      <th className="px-3 py-2 text-right">{t(`${p}.columns.manualChoices`, {}, 'Manuelle valg')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.id} onClick={() => setSelected(item)}
                        className={`border-b border-gray-200 cursor-pointer ${selected?.id === item.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(item.created)}<div className="text-xs text-gray-500">{item.createdBy}</div></td>
                        <td className="px-3 py-2">{item.documentNo ?? '–'}<div className="text-xs text-gray-500">{item.fileName}</div></td>
                        <td className="px-3 py-2">{item.supplierName ?? '–'}<div className="text-xs text-gray-500">{item.supplierNo}</div></td>
                        <td className="px-3 py-2">
                          {item.method === 'Script'
                            ? t(`${p}.method.script`, {}, 'Skript')
                            : item.method === 'AI' ? t(`${p}.method.ai`, {}, 'AI') : '–'}
                          {item.scriptAttempted && item.method !== 'Script' && (
                            <div className="text-xs text-gray-500">{t(`${p}.method.scriptFailed`, {}, 'skript feilet')}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">{statusText(item.status)}</td>
                        <td className="px-3 py-2 text-right">{item.lineCount}{item.excludedCount > 0 && <span className="text-gray-500"> (−{item.excludedCount})</span>}</td>
                        <td className="px-3 py-2 text-right">{item.manualChoiceCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between px-3 py-2 text-sm text-gray-600">
                  <span>{t(`${p}.paging`, { '0': offset + 1, '1': Math.min(offset + PAGE_SIZE, total), '2': total },
                    `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} av ${total}`)}</span>
                  <span className="flex gap-2">
                    <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                      className="px-3 py-1 border border-gray-300 rounded-lg disabled:text-gray-400">‹</button>
                    <button type="button" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}
                      className="px-3 py-1 border border-gray-300 rounded-lg disabled:text-gray-400">›</button>
                  </span>
                </div>
              </div>

              {selected && (
                <div className="lg:w-96 bg-white border border-gray-200 rounded-lg p-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold text-gray-800">{selected.documentNo ?? selected.fileName}</h2>
                    <button type="button" onClick={() => setOpenId(selected.id)}
                      className="px-3 py-2 bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 flex items-center gap-1">
                      <ExternalLink size={14} />{t(`${p}.open`, {}, 'Åpne')}
                    </button>
                  </div>
                  {selected.aiModel && <div className="text-gray-600">{t(`${p}.aiModel`, { '0': selected.aiModel }, `Modell: ${selected.aiModel}`)}</div>}
                  {selected.approvedBy && (
                    <div className="text-gray-600">
                      {t(`${p}.approvedBy`, { '0': selected.approvedBy, '1': fmtDateTime(selected.approvedAt) },
                        `Godkjent av ${selected.approvedBy} ${fmtDateTime(selected.approvedAt)}`)}
                    </div>
                  )}
                  <div className="font-semibold text-gray-800">{t(`${p}.eventsTitle`, {}, 'Hendelser og manuelle valg')}</div>
                  {events == null ? (
                    <Loader className="animate-spin" size={16} />
                  ) : (
                    <ul className="space-y-2">
                      {events.map(e => (
                        <li key={e.id} className="border-l-2 border-gray-200 pl-3">
                          <div className="text-gray-800">{eventText(e.eventType)}
                            {e.eventType === 'QuantityChosen' && e.fieldName && <span> · {valueText(e.eventType, e.fieldName)}</span>}
                            {e.lineId != null && <span className="text-gray-500"> · {lineText(e)}</span>}
                          </div>
                          {(e.oldValue || e.newValue) && (
                            <div className="text-xs text-gray-600">{valueText(e.eventType, e.oldValue)} → {valueText(e.eventType, e.newValue)}</div>
                          )}
                          <div className="text-xs text-gray-500">{e.operatorId} · {fmtDateTime(e.created)}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )
        ) : maps == null ? (
          <div className="flex items-center justify-center h-32"><Loader className="animate-spin" size={20} /></div>
        ) : maps.length === 0 ? (
          <p className="text-sm text-gray-500">{t(`${p}.itemMaps.empty`, {}, 'Ingen lærte koblinger.')}</p>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                  <th className="px-3 py-2">{t(`${p}.itemMaps.supplierNo`, {}, 'Leverandør')}</th>
                  <th className="px-3 py-2">{t(`${p}.itemMaps.supplierItemNo`, {}, 'Leverandørens varenr.')}</th>
                  <th className="px-3 py-2">{t(`${p}.itemMaps.itemNo`, {}, 'Vårt varenr.')}</th>
                  <th className="px-3 py-2 text-right">{t(`${p}.itemMaps.useCount`, {}, 'Brukt')}</th>
                  <th className="px-3 py-2">{t(`${p}.itemMaps.created`, {}, 'Lært')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {maps.map(m => (
                  <tr key={m.id} className="border-b border-gray-200">
                    <td className="px-3 py-2">{m.supplierNo}</td>
                    <td className="px-3 py-2">{m.supplierItemNo}</td>
                    <td className="px-3 py-2">{m.itemNo}</td>
                    <td className="px-3 py-2 text-right">{m.useCount}</td>
                    <td className="px-3 py-2">{fmtDateTime(m.created)}<div className="text-xs text-gray-500">{m.createdBy}</div></td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" onClick={() => removeMap(m)} className="p-2 rounded-lg text-red-700 hover:bg-red-50"
                        aria-label={t(`${p}.itemMaps.delete`, {}, 'Fjern koblingen')}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocInterpretationLog;
